<template>
    <Teleport defer to="#agent-model-overlay" :disabled="!expanded">
        <article class="message-agent" :class="{ 'message-agent-expanded': expanded }" :data-message-id="message.id" aria-label="Agent response">
            <v-card class="message-agent-bubble" color="darkerbase" rounded="xl" elevation="0">
                <div class="message-agent-viewer">
                    <ModelViewer
                        :message-id="message.id"
                        :asset="message.asset"
                        :pending="message.pending"
                        :failed="message.state === 'error'"
                        @ready-change="viewerLoaded = $event" />
                    <v-btn
                        v-if="expanded || canMaximize"
                        ref="expandButton"
                        class="message-agent-expand"
                        icon
                        size="small"
                        variant="flat"
                        color="darkbase"
                        :aria-label="expanded ? 'Minimize model viewer' : 'Maximize model viewer'"
                        :aria-expanded="expanded"
                        @click="toggleExpanded">
                        <v-icon :icon="expanded ? 'fa-solid fa-down-left-and-up-right-to-center' : 'fa-solid fa-up-right-and-down-left-from-center'" />
                        <v-tooltip :disabled="$vuetify.display.xs || $vuetify.display.platform.touch" activator="parent" location="end">{{ expanded ? 'Minimize viewer' : 'Maximize viewer' }}</v-tooltip>
                    </v-btn>
                </div>
                <ModelStatus
                    :status="message.status"
                    :pending="message.pending"
                    :ready="message.state === 'ready'"
                    :error="message.error"
                    :uncertain="message.uncertain"
                    :busy="busy"
                    :checking="checking"
                    @retry="$emit('retry')"
                    @check-status="$emit('check-status')"
                    @download="openDialog('download')"
                    @share="openDialog('share')" />
            </v-card>
        </article>
    </Teleport>
</template>

<script>
import { mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import { defineAsyncComponent } from 'vue'
import ModelStatus from '../model/ModelStatus.vue'

export default {
    name: 'MessageAgent',
    components: {
        ModelViewer: defineAsyncComponent(() => import('../viewer/ModelViewer.vue')),
        ModelStatus
    },
    props: {
        message: { type: Object, required: true },
        busy: { type: Boolean, default: false },
        checking: { type: Boolean, default: false },
        expanded: { type: Boolean, default: false }
    },
    emits: ['retry', 'check-status', 'expand'],
    data() {
        return { viewerLoaded: false }
    },
    computed: {
        canMaximize() {
            return this.message.state === 'ready' && !this.message.pending && this.viewerLoaded &&
                !!this.message.asset?.files?.default?.data
        }
    },
    watch: {
        expanded() {
            this.$nextTick(() => this.$refs.expandButton?.$el.focus())
        }
    },
    methods: {
        ...mapActions('dialogs', ['open']),
        toggleExpanded() {
            if (this.expanded || this.canMaximize) this.$emit('expand')
        },
        openDialog(component) {
            if (this.message.state !== 'ready' || !this.message.asset) return
            this.open({ component, payload: { asset: this.message.asset } })
        }
    }
}
</script>

<style scoped>
.message-agent {
    width: 100%;
    max-width: 720px;
    min-width: 0;
}

.message-agent-bubble {
    animation: agent-message-in var(--agent-motion-duration, 300ms) var(--agent-motion-easing, ease-out);
    overflow: hidden;
    border: 1px solid rgb(var(--v-theme-lightbase));
    border-bottom-left-radius: 6px !important;
}

.message-agent-viewer {
    position: relative;
    min-height: 280px;
}

.message-agent-expand {
    position: absolute;
    top: 12px;
    left: 12px;
    z-index: 1;
}

.message-agent-expand .v-icon {
    transform: scaleX(-1);
}

.message-agent-expanded {
    height: 100%;
    max-width: none;
}

.message-agent-expanded .message-agent-bubble {
    animation-name: agent-viewer-expand;
    height: 100%;
    display: flex;
    flex-direction: column;
    border-radius: 0 !important;
}

.message-agent-expanded .message-agent-viewer {
    flex: 1;
    min-height: 0;
}

.message-agent-expanded :deep(.model-viewer) {
    height: 100%;
    min-height: 0;
}
</style>
